import { useEffect, useState } from "react";
import { GraduationCap, LogOut, Bell, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const BASE_TABS = [
  { id: "home", label: "Overview & Waitlist" },
  { id: "syllabus", label: "AI Syllabus Architect" },
  { id: "socratic", label: "Socratic AI Tutor" },
  { id: "essay", label: "Essay Lab" },
  { id: "impact", label: "Global Impact" },
];

function ReminderBell({ onNavigate }) {
  const { user, refreshUser } = useAuth();
  const userId = user && user.id;
  const [data, setData] = useState({ overdue: [], soon: [], count: 0 });
  const [days, setDays] = useState(String(user?.reminder_days || 3));

  const load = () => api.get("/deadlines/reminders")
    .then(({ data }) => setData(data)).catch(() => {});

  useEffect(() => {
    let alive = true;
    const run = () => api.get("/deadlines/reminders")
      .then(({ data }) => alive && setData(data)).catch(() => {});
    run();
    const t = setInterval(run, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [userId]);

  useEffect(() => { setDays(String(user?.reminder_days || 3)); }, [user?.reminder_days]);

  const changeDays = async (val) => {
    setDays(val);
    try {
      await api.patch("/settings/reminders", { days: Number(val) });
      await refreshUser();
      await load();
      toast.success(`We'll nudge you ${val} days ahead.`);
    } catch {
      toast.error("Could not update reminder timing.");
    }
  };

  const fmt = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="reminder-bell" className="relative h-9 w-9 rounded-md flex items-center justify-center text-slate-500 hover:text-navy hover:bg-navy-soft transition-colors">
          <Bell className="h-5 w-5" />
          {data.count > 0 && (
            <span data-testid="reminder-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-persian-rose text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
              {data.count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="reminder-panel">
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="font-serif text-lg text-navy">Study reminders</p>
          <p className="text-xs text-slate-500">Milestones due within {data.days || days} days</p>
        </div>
        <div className="max-h-72 overflow-y-auto p-2">
          {data.count === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">You're all caught up. Nothing due soon.</p>
          ) : (
            <>
              {data.overdue.map((d) => (
                <button key={d.id} onClick={() => onNavigate("syllabus")}
                  className="w-full text-left rounded-lg p-2.5 hover:bg-navy-soft flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-persian-rose shrink-0" />
                  <span>
                    <span className="text-sm font-medium text-navy block">{d.title}</span>
                    <span className="text-xs text-persian-rose font-medium">Overdue · {fmt(d.date)}</span>
                  </span>
                </button>
              ))}
              {data.soon.map((d) => (
                <button key={d.id} onClick={() => onNavigate("syllabus")}
                  className="w-full text-left rounded-lg p-2.5 hover:bg-navy-soft flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-persian-blue shrink-0" />
                  <span>
                    <span className="text-sm font-medium text-navy block">{d.title}</span>
                    <span className="text-xs text-slate-500">Due {fmt(d.date)} · {d.exam}</span>
                  </span>
                </button>
              ))}
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500">Remind me ahead by</span>
          <Select value={days} onValueChange={changeDays}>
            <SelectTrigger data-testid="reminder-days-select" className="h-8 w-28 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 5, 7, 14].map((n) => (
                <SelectItem key={n} value={String(n)} data-testid={`reminder-days-${n}`}>{n} days</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function Header({ active, onNavigate, onAuth }) {
  const { user, logout } = useAuth();
  const tabs = [...BASE_TABS];
  if (user && user.role === "admin") tabs.push({ id: "admin", label: "Subscriber Admin" });

  const openBilling = async () => {
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open billing portal.");
    }
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <button onClick={() => onNavigate("home")} data-testid="brand-logo" className="flex items-center gap-3 group">
            <div className="h-9 w-9 rounded-lg bg-navy flex items-center justify-center ring-1 ring-persian-blue/40 group-hover:ring-persian-blue transition-all">
              <GraduationCap className="h-5 w-5 text-persian-blue" />
            </div>
            <div className="text-left leading-tight">
              <div className="font-serif font-bold text-navy text-lg tracking-tight">OCC</div>
              <div className="hidden sm:block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">Cognitive Capital</div>
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => onNavigate(t.id)} data-testid={`nav-tab-${t.id}`}
                className={`relative px-3.5 py-2 text-sm font-medium rounded-md transition-colors ${
                  active === t.id ? "text-navy" : "text-slate-500 hover:text-navy"
                }`}>
                {t.label}
                {active === t.id && <span className="absolute left-3 right-3 -bottom-[1px] h-[2.5px] bg-persian-blue rounded-full" />}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <ReminderBell onNavigate={onNavigate} />
                {user.premium && (
                  <Button variant="ghost" size="sm" onClick={openBilling} data-testid="manage-plan-button"
                    className="hidden sm:inline-flex text-slate-500 hover:text-navy">
                    <CreditCard className="h-4 w-4 sm:mr-1.5" /> <span className="hidden md:inline">Manage plan</span>
                  </Button>
                )}
                <span className="hidden sm:inline text-sm text-slate-600">{user.name}</span>
                <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button" className="text-slate-500 hover:text-navy">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => onAuth("login")} data-testid="header-login-button" className="text-navy hover:bg-navy-soft">Sign In</Button>
                <Button size="sm" onClick={() => onAuth("register")} data-testid="header-register-button" className="bg-navy hover:bg-navy-light text-white">Enrol</Button>
              </>
            )}
          </div>
        </div>

        <nav className="lg:hidden flex items-center gap-1 overflow-x-auto pb-2 -mb-px">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => onNavigate(t.id)} data-testid={`nav-tab-mobile-${t.id}`}
              className={`whitespace-nowrap px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                active === t.id ? "border-persian-blue text-navy bg-persian-blue/10" : "border-slate-200 text-slate-500"
              }`}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
}
